import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const Bubble = ({ delay, size, left }) => (
  <span
    className="bubble"
    style={{
      width: size,
      height: size,
      left: `${left}%`,
      animationDelay: `${delay}s`,
      animationDuration: `${6 + Math.random() * 6}s`,
    }}
  />
);

export default function Hero() {
  return (
    <section className="relative overflow-hidden bg-neutral-950 text-white flex items-center py-10 lg:py-14" data-testid="hero-section">
      <div className="absolute inset-0 opacity-40">
        <div className="absolute inset-0" style={{
          background: "radial-gradient(ellipse at 20% 20%, rgba(212,175,55,0.25), transparent 40%), radial-gradient(ellipse at 85% 70%, rgba(200,30,30,0.15), transparent 40%), radial-gradient(ellipse at 60% 40%, rgba(15,118,110,0.15), transparent 40%)"
        }} />
      </div>
      <div className="noise-overlay" />
      {[...Array(14)].map((_, i) => (
        <Bubble key={i} delay={i * 0.6} size={`${10 + Math.random() * 40}px`} left={5 + i * 6.5} />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center py-10 lg:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-6 space-y-8"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-px bg-gold-400" />
            <span className="eyebrow text-gold-400">Scitygate Brewing Company Ltd</span>
          </div>
          <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Great Taste.
            <br />
            <span className="gold-gradient-text">Brighter Tomorrows.</span>
          </h1>
          <p className="text-lg text-neutral-300 leading-relaxed max-w-xl">
            Refreshing People. Empowering Communities. Building a Brighter Tomorrow.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/brands" className="group inline-flex items-center gap-2 bg-gold-400 hover:bg-gold-300 text-neutral-950 rounded-full px-6 py-3.5 text-sm font-semibold transition-all uppercase tracking-widest" data-testid="hero-explore-brands">
              Explore Our Brands
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/distributor" className="inline-flex items-center gap-2 border border-white/20 hover:border-gold-400 text-white rounded-full px-6 py-3.5 text-sm font-semibold uppercase tracking-widest transition-colors" data-testid="hero-become-distributor">
              Become a Distributor
            </Link>
            <Link to="/contact?topic=partnership" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-semibold px-4 py-3.5 uppercase tracking-widest transition-colors" data-testid="hero-partner-sbc">
              Partner With SBC →
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/10 max-w-lg">
            <Stat n="5" label="Brands In Development" />
            <Stat n="2026" label="Incorporated UK" />
            <Stat n="Manchester" label="Headquarters" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative w-full">
            <img
              src="/api/images/sbc-master-hero.png"
              alt="SBC Ltd family of premium beverage brands — OneCola, OneFresh, Maltora, VoltX"
              className="w-full h-auto object-contain drop-shadow-[0_30px_60px_rgba(212,175,55,0.15)]"
              data-testid="hero-master-composition"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const Stat = ({ n, label }) => (
  <div>
    <div className="font-display font-bold text-3xl gold-gradient-text">{n}</div>
    <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-1">{label}</div>
  </div>
);
